import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import PhoneInput from '../components/PhoneInput';
import { usePhoneValidation, usePhoneApiError } from '../hooks/usePhoneValidation';

// Feature flag to control default authentication method
const USE_EMAIL_ONLY = import.meta.env.VITE_USE_EMAIL_ONLY === 'true';

const LoginPage: React.FC = () => {
  const { passwordlessLogin, verifyOTP, resendOTP, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState<'login' | 'otp'>('login');
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [session, setSession] = useState<string>('');
  const [identifierType, setIdentifierType] = useState<'phone' | 'email'>(USE_EMAIL_ONLY ? 'email' : 'phone');
  const [showEmailOption, setShowEmailOption] = useState(USE_EMAIL_ONLY);
  
  // Phone validation hooks
  const phoneValidation = usePhoneValidation();
  const { apiError: phoneApiError, handleApiError, clearError } = usePhoneApiError();

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (identifierType === 'phone' && !phoneValidation.isPhoneValid) {
      return;
    }
    
    if (identifierType === 'email' && !identifier.trim()) {
      return;
    }

    // Clear any previous API errors
    clearError();

    const loginData = identifierType === 'email' 
      ? { email: identifier.trim() }
      : { phone: phoneValidation.formattedPhone };

    try {
      const result = await passwordlessLogin(loginData);
      
      if (result.success && result.session) {
        setSession(result.session);
        setStep('otp');
      }
    } catch (error: any) {
      // Handle phone-specific API errors
      const errorMessage = handleApiError(error);
      if (errorMessage.includes('phone') || errorMessage.includes('número') || errorMessage.includes('teléfono')) {
        phoneValidation.setApiError(errorMessage);
      }
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp.trim()) {
      return;
    }

    const verifyData = {
      code: otp.trim(),
      session,
      ...(identifierType === 'email' ? { email: identifier } : { phone: identifier })
    };

    await verifyOTP(verifyData);
  };

  const handleResendOTP = async () => {
    const loginData = identifierType === 'email' 
      ? { email: identifier }
      : { phone: phoneValidation.formattedPhone };

    await resendOTP(loginData);
  };

  const handleBackToLogin = () => {
    setStep('login');
    setOtp('');
    setSession('');
  };

  const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIdentifier(value);
    
    // Auto-detect if it's a phone number or email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
    
    if (emailRegex.test(value)) {
      setIdentifierType('email');
    } else if (phoneRegex.test(value) && value.length >= 10) {
      setIdentifierType('phone');
    }
  };

  const handlePhoneChange = (value: string, validation: any) => {
    phoneValidation.setPhoneNumber(value);
    setIdentifier(value);
    setIdentifierType('phone');
  };

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {step === 'login' ? t('signInTo') : t('verifyCode')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {step === 'login' ? t('timeTrackingMadeSimple') : t('enterVerificationCode')}
          </p>
        </div>
        
        {step === 'login' ? (
          <form className="mt-8 space-y-6" onSubmit={handleLoginSubmit}>
            <div className="space-y-4">
              <div>
                {identifierType === 'email' ? (
                  <>
                    <label htmlFor="identifier" className="block text-sm font-medium text-gray-700">
                      {t('emailAddress')}
                    </label>
                    <input
                      id="identifier"
                      name="identifier"
                      type="email"
                      autoComplete="email"
                      required
                      className="input-field mt-1"
                      placeholder={t('enterEmail')}
                      value={identifier}
                      onChange={handleIdentifierChange}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {t('weWillSendCodeToEmail')}
                    </p>
                  </>
                ) : (
                  <PhoneInput
                    value={phoneValidation.phoneNumber}
                    onChange={handlePhoneChange}
                    label={t('phoneNumber')}
                    required
                    error={phoneApiError || undefined}
                    id="identifier"
                    name="identifier"
                  />
                )}
              </div>

              {/* Email option toggle - only show if not email-only mode */}
              {!USE_EMAIL_ONLY && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailOption(!showEmailOption);
                      if (!showEmailOption) {
                        setIdentifierType('email');
                        setIdentifier('');
                      } else {
                        setIdentifierType('phone');
                        setIdentifier('');
                      }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    {showEmailOption ? t('usePhoneInstead') : t('useEmailInstead')}
                  </button>
                </div>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || (identifierType === 'phone' ? !phoneValidation.isPhoneValid : !identifier.trim())}
                className="btn-primary w-full flex justify-center py-2 px-4"
              >
                {isLoading ? t('sendingCode') : t('sendCode')}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                {t('dontHaveAccount')}{' '}
                <Link
                  to="/register"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  {t('registerHere')}
                </Link>
              </p>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleOTPSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                  {t('verificationCode')}
                </label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  className="input-field mt-1 text-center text-2xl tracking-widest"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('codeSentTo')} {identifier}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={isLoading || otp.length !== 6}
                className="btn-primary w-full flex justify-center py-2 px-4"
              >
                {isLoading ? t('verifying') : t('verifyCode')}
              </button>
              
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isLoading}
                className="w-full text-sm text-blue-600 hover:text-blue-500 disabled:text-gray-400"
              >
                {t('resendCode')}
              </button>
              
              <button
                type="button"
                onClick={handleBackToLogin}
                disabled={isLoading}
                className="w-full text-sm text-gray-600 hover:text-gray-500 disabled:text-gray-400"
              >
                {t('backToLogin')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage; 